import { useEffect } from 'react';
import Card from './foundational/Card';
import Button from './foundational/Button';
import { trackEvent } from '../lib/eventTracking';

const COPPER = '#b8722d';

// Set en memoria del módulo: dedupe nudge_mostrado dentro de la sesión.
// Refresh de página → nuevo Set. Coincide con la noción de "sesión" desde
// la perspectiva del usuario.
const seenInSession = new Set();

/**
 * NudgeCard — tarjeta para nudges modo 'card' del Home.
 *
 * Props:
 *   nudge      — objeto del catálogo (id, copy, action, dismissible).
 *   onAction   — callback (recibe nudge). Lo provee el consumer (Home),
 *                típicamente useNudges.executeAction(nudge, navigator).
 *   onDismiss  — callback (recibe nudge.id). Si dismissible, se renderiza
 *                el botón "Ahora no" que lo invoca.
 *
 * Dispara nudge_mostrado una vez por sesión por id.
 */
export default function NudgeCard({ nudge, onAction, onDismiss }) {
  useEffect(() => {
    if (!nudge?.id) return;
    if (seenInSession.has(nudge.id)) return;
    seenInSession.add(nudge.id);
    trackEvent('nudge_mostrado', {
      modulo: nudge.scope === 'tour' ? 'onboarding' : nudge.scope,
      payload: { nudge_id: nudge.id, scope: nudge.scope, mode: nudge.mode },
    });
  }, [nudge?.id, nudge?.scope, nudge?.mode]);

  if (!nudge) return null;

  return (
    <Card padding="md">
      <p
        className="font-sans font-semibold tracking-wide"
        style={{ color: COPPER, fontSize: 'calc(11px * var(--texto-escala, 1))', letterSpacing: '0.08em' }}
      >
        {nudge.copy.tag}
      </p>
      <p
        className="font-sans font-semibold text-ink mt-2"
        style={{ fontSize: 'calc(15px * var(--texto-escala, 1))', lineHeight: '1.35' }}
      >
        {nudge.copy.titulo}
      </p>
      <p
        className="font-sans text-ink-subtle mt-1.5 leading-relaxed"
        style={{ fontSize: 'calc(13px * var(--texto-escala, 1))' }}
      >
        {nudge.copy.descripcion}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          size="md"
          onClick={() => onAction?.(nudge)}
        >
          {nudge.copy.cta_label}
        </Button>
        {nudge.dismissible && (
          <Button
            variant="ghost"
            size="md"
            onClick={() => onDismiss?.(nudge.id)}
          >
            {nudge.copy.dismiss_label || 'Ahora no'}
          </Button>
        )}
      </div>
    </Card>
  );
}

// Solo para tests: permite resetear el dedupe entre casos.
export function __resetSeenInSession() {
  seenInSession.clear();
}
